window.BellyfulPlanLabel = function BellyfulPlanLabel(value = '') {
  let label = String(value).replace(/~~/g, '').trim();
  const named = label.match(/(?:^|,)N-([^,]+)$/i);
  if (named?.[1]) return named[1].trim();
  label = label.replace(/^.*?[,;:]\s*/, '').replace(/^(save|subscribe(?:\s*&\s*save)?)\s*[-,:–—]\s*/i, '');
  return label.trim();
};

class BellyfulTheme {
  constructor() {
    this.lastFocus = null;
    this.abortController = null;
    this.cartCelebratePending = false;
    this.cartCelebrationContext = null;
    this.cartScrollTimer = null;
    this.mountMobileMenu();
    this.bind();
    this.cleanPlanLabels(document);
    this.setupCartScrollbar(document);
    const cartStatus = document.querySelector('[data-page-cart-status]');
    if (cartStatus) cartStatus.textContent = 'Quantities update automatically.';
  }


  // Mount fixed mobile navigation outside the frosted header's containing block.
  mountMobileMenu() {
    ['BellyfulMobileNav', 'BellyfulSearch'].forEach(id => {
      const drawer = document.querySelector(`header.bf-header #${id}`);
      if (!drawer) return;
      const previous = document.querySelector(`body > #${id}`);
      if (previous && previous !== drawer) previous.remove();
      document.body.appendChild(drawer);
    });
  }

  bind() {
    document.addEventListener('shopify:section:load', event => {
      if (event.target.querySelector?.('header.bf-header')) this.mountMobileMenu();
    });
    document.addEventListener('click', (event) => {
      const pageRemove = event.target.closest('[data-page-cart-remove]');
      if (pageRemove) {
        event.preventDefault();
        this.removeCartPageItem(pageRemove);
        return;
      }
      const pageQuantity = event.target.closest('[data-page-quantity]');
      if (pageQuantity) {
        const input = pageQuantity.closest('.bf-page-qty')?.querySelector('input');
        if (input) {
          const current = Number(input.value);
          input.value = Math.max(0, (Number.isFinite(current) ? current : 0) + Number(pageQuantity.dataset.pageQuantity));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
      const cartOpen = event.target.closest('[data-cart-open]');
      if (cartOpen) {
        event.preventDefault();
        this.openDrawer('#BellyfulCartDrawer', cartOpen);
        return;
      }

      const cartClose = event.target.closest('[data-cart-close]');
      if (cartClose) {
        event.preventDefault();
        this.closeDrawer('#BellyfulCartDrawer');
        return;
      }

      const searchOpen = event.target.closest('[data-search-open]');
      if (searchOpen) {
        event.preventDefault();
        this.openDrawer('#BellyfulSearch', searchOpen, '[data-predictive-search-input]');
        return;
      }

      const searchClose = event.target.closest('[data-search-close]');
      if (searchClose) {
        event.preventDefault();
        this.closeDrawer('#BellyfulSearch');
        return;
      }

      const menuOpen = event.target.closest('[data-menu-open]');
      if (menuOpen) {
        event.preventDefault();
        this.openDrawer('#BellyfulMobileNav', menuOpen, '[data-menu-first-link]');
        return;
      }

      const menuClose = event.target.closest('[data-menu-close]');
      if (menuClose) {
        event.preventDefault();
        this.closeDrawer('#BellyfulMobileNav');
        return;
      }

      const quantityButton = event.target.closest('[data-cart-quantity]');
      if (quantityButton) {
        event.preventDefault();
        this.changeCartQuantity(quantityButton);
        return;
      }

      const removeButton = event.target.closest('[data-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        this.changeCartQuantity(removeButton, 0);
        return;
      }

      const giftDecline = event.target.closest('[data-cart-decline-gift]');
      if (giftDecline) {
        event.preventDefault();
        document.querySelectorAll('.bf-cart-gift-modal, .bf-cart-gift-offer').forEach(el => el.hidden = true);
        this.giftDeclined = true;
        return;
      }
      const giftButton = event.target.closest('[data-cart-add-gift]');
      if (giftButton) {
        event.preventDefault();
        this.addCartGift(giftButton);
      }
    });

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-cart-discount-form]');
      if (!form) return;
      event.preventDefault();
      this.applyCartDiscount(form);
    });

    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-cart-selling-plan]')) {
        this.changeCartPlan(event.target);
        return;
      }
      if (!event.target.matches('[data-cart-page-form] input[name="updates[]"]')) return;
      const input = event.target;
      const value = Number(input.value);
      if (!Number.isInteger(value) || value < 0) { input.reportValidity(); return; }
      const control = input.closest('.bf-cart-page-line')?.querySelector('[data-page-cart-remove]');
      if (control) this.removeCartPageItem(control, value);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      this.closeDrawer('#BellyfulSearch');
      this.closeDrawer('#BellyfulMobileNav');
      this.closeDrawer('#BellyfulCartDrawer');
    });
  }

  cleanPlanLabels(root) {
    root.querySelectorAll('[data-plan-label]').forEach((element) => {
      const clean = window.BellyfulPlanLabel(element.dataset.planLabel);
      if (element.tagName === 'OPTION') {
        const price = element.textContent.split('—').pop()?.trim();
        element.textContent = price ? `${clean} — ${price}` : clean;
      } else {
        element.textContent = clean;
      }
    });
  }

  async resolveCartLineKey(control, preferredKey, strict = false) {
    const variantId = Number(control?.dataset?.cartVariantId || 0);
    const planId = Number(control?.dataset?.cartPlanId || 0);
    if (!variantId) return preferredKey || '';
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) {
        if (strict) throw new Error('Unable to check your bag. Please try again.');
        return preferredKey || '';
      }
      const cart = await response.json();
      const exact = (cart.items || []).find(item => item.key === preferredKey);
      if (exact && Number(exact.variant_id) === variantId && Number(exact.selling_plan_allocation?.selling_plan?.id || 0) === planId) return exact.key;
      const rawProperties = control?.closest('[data-cart-properties]')?.dataset.cartProperties;
      if (rawProperties == null) return '';
      let properties;
      try { properties = JSON.parse(rawProperties) || {}; } catch (_) { return ''; }
      const fingerprint = value => JSON.stringify(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)));
      const matches = (cart.items || []).filter(item =>
        Number(item.variant_id) === variantId &&
        Number(item.selling_plan_allocation?.selling_plan?.id || 0) === planId &&
        fingerprint(item.properties) === fingerprint(properties));
      // Multiple matches are ambiguous. The customer can refresh rather than risk a wrong-line edit.
      return matches.length === 1 ? matches[0].key : '';
    } catch (error) {
      if (strict) throw error;
      return preferredKey || '';
    }
  }

  friendlyCartError(error) {
    const raw = String(error?.message || error || '');
    if (/no valid id or line parameter|invalid.*line|invalid.*id/i.test(raw)) return 'We couldn’t update your bag. Please try again.';
    return raw || 'We couldn’t update your bag. Please try again.';
  }

  async changeCartPlan(select) {
    let key = select.dataset.cartKey;
    const quantity = Number(select.dataset.cartCurrent || 1);
    const sellingPlan = select.value ? Number(select.value) : null;
    const message = document.querySelector('[data-cart-message]');
    if (!key || this.cartUpdating) return;
    this.cartUpdating = true;
    if (sellingPlan && !Number(document.querySelector('#BellyfulCartDrawer')?.dataset.subscriptionCount || 0)) this.prepareCartCelebration();
    else { this.cartCelebratePending = false; this.cartCelebrationContext = null; }
    this.cartFocus = { key, action: 'data-cart-selling-plan' };
    select.disabled = true;
    if (message) message.textContent = 'Updating your purchase choice…';
    try {
      key = await this.resolveCartLineKey(select, key);
      if (!key) throw new Error('We couldn’t find that bag item.');
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, selling_plan: sellingPlan, sections: 'cart-drawer', sections_url: window.location.pathname })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to update this purchase choice.');
      await window.BellyfulReplaceCart(await this.reconcileCartGift(data));
    } catch (error) {
      if (message) message.textContent = this.friendlyCartError(error);
      this.cartCelebratePending = false;
      this.cartCelebrationContext = null;
      select.disabled = false;
    } finally {
      this.cartUpdating = false;
      this.cartFocus = null;
    }
  }

  openDrawer(selector, trigger, focusSelector) {
    const drawer = document.querySelector(selector);
    if (!drawer) return;

    this.closeOtherDrawers(selector);
    this.lastFocus = trigger || document.activeElement;
    drawer.hidden = false;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('bf-lock-scroll');

    if (trigger?.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      const focusTarget = focusSelector
        ? drawer.querySelector(focusSelector)
        : drawer.querySelector('[data-drawer-panel], button, a, input, select, textarea');
      focusTarget?.focus({ preventScroll: true });
    });

    this.enableFocusTrap(drawer);
  }

  closeDrawer(selector, restoreFocus = true) {
    const drawer = document.querySelector(selector);
    if (!drawer || drawer.getAttribute('aria-hidden') === 'true') return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.hidden = true;
    if (selector === '#BellyfulCartDrawer') window.BellyfulCartFX?.stop();
    this.disableFocusTrap();

    const anyOpen = document.querySelector('.bf-drawer.is-open');
    if (!anyOpen) document.documentElement.classList.remove('bf-lock-scroll');

    document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((control) => {
      if (control.hasAttribute('aria-expanded')) control.setAttribute('aria-expanded', 'false');
    });

    if (restoreFocus && this.lastFocus instanceof HTMLElement) {
      this.lastFocus.focus({ preventScroll: true });
    }
  }

  closeOtherDrawers(exceptionSelector) {
    document.querySelectorAll('.bf-drawer.is-open').forEach((drawer) => {
      if (`#${drawer.id}` !== exceptionSelector) this.closeDrawer(`#${drawer.id}`, false);
    });
  }

  enableFocusTrap(container) {
    this.disableFocusTrap();
    this.abortController = new AbortController();
    container.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusables = [...container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => element.tabIndex >= 0 && !element.closest('[hidden], [inert], [aria-hidden="true"]') && element.getClientRects().length > 0);

      if (!focusables.length) { event.preventDefault(); container.querySelector('[data-drawer-panel]')?.focus(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && (document.activeElement === first || document.activeElement === container.querySelector('[data-drawer-panel]'))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }, { signal: this.abortController.signal });
  }

  disableFocusTrap() {
    this.abortController?.abort();
    this.abortController = null;
  }

  // The full cart can outlive its item keys after drawer edits or discount changes.
  // Resolve the same variant, purchase plan and properties before removing anything.
  async removeCartPageItem(control, quantity = 0) {
    if (this.cartUpdating) return;
    this.cartUpdating = true;
    const form = control.closest('[data-cart-page-form]');
    const status = form?.querySelector('[data-page-cart-status]');
    const controls = [...(form?.querySelectorAll('button, input, select') || [])];
    const disabled = controls.map(element => element.disabled);
    controls.forEach(element => element.disabled = true);
    control.setAttribute('aria-disabled', 'true');
    form?.setAttribute('aria-busy', 'true');
    if (status) status.textContent = quantity ? 'Updating your bag…' : 'Removing item…';
    const root = window.Shopify?.routes?.root || '/';
    try {
      const key = await this.resolveCartLineKey(control, control.dataset.cartKey, true);
      if (!key) {
        // Already removed or ambiguous: refresh; never guess a different line.
        window.location.assign(`${root}cart`);
        return;
      }
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to remove this item. Please refresh your bag and try again.');
      await this.reconcileCartGift(data);
      window.location.assign(`${root}cart`);
    } catch (error) {
      if (status) status.textContent = this.friendlyCartError(error);
      form?.querySelectorAll('input[name="updates[]"]').forEach(input => input.value = input.defaultValue);
    } finally {
      this.cartUpdating = false;
      control.removeAttribute('aria-disabled');
      form?.removeAttribute('aria-busy');
      controls.forEach((element, index) => element.disabled = disabled[index]);
    }
  }

  async changeCartQuantity(button, forcedQuantity) {
    let key = button.dataset.cartKey;
    const current = Number(button.dataset.cartCurrent || 0);
    const delta = Number(button.dataset.cartQuantity || 0);
    const quantity = forcedQuantity ?? Math.max(0, current + delta);
    const message = document.querySelector('[data-cart-message]');

    if (!key || this.cartUpdating) return;
    this.cartUpdating = true;
    if (quantity > current && !button.closest('[data-bellyful-gift-line]')) this.prepareCartCelebration();
    else { this.cartCelebratePending = false; this.cartCelebrationContext = null; }
    this.cartFocus = document.activeElement === button ? {
      key, action: button.hasAttribute('data-cart-remove') ? 'data-cart-remove' : 'data-cart-quantity',
      delta: button.dataset.cartQuantity
    } : null;
    button.disabled = true;
    if (message) message.textContent = 'Updating bag…';

    try {
      key = await this.resolveCartLineKey(button, key);
      if (!key) throw new Error('We couldn’t find that bag item.');
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: 'cart-drawer',
          sections_url: window.location.pathname
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to update bag.');

      await window.BellyfulReplaceCart(await this.reconcileCartGift(data));
      if (message) message.textContent = '';
    } catch (error) {
      this.cartCelebratePending = false;
      this.cartCelebrationContext = null;
      if (message) message.textContent = this.friendlyCartError(error);
    } finally {
      this.cartUpdating = false;
      this.cartFocus = null;
      button.disabled = false;
    }
  }

  prepareCartCelebration() {
    const countNode = document.querySelector('[data-cart-count]');
    const drawer = document.querySelector('#BellyfulCartDrawer');
    this.cartCelebrationContext = {
      count: Number(drawer?.dataset.realItemCount || countNode?.textContent || 0),
      subscriptions: Number(drawer?.dataset.subscriptionCount || 0),
      total: Number(drawer?.dataset.cartTotal || 0)
    };
    this.cartCelebratePending = true;
  }

  setupCartScrollbar(root = document) {
    const content = root.querySelector?.('.bf-cart-drawer__content');
    if (!content || content.dataset.scrollFadeReady === 'true') return;
    content.dataset.scrollFadeReady = 'true';
    let timer;
    content.addEventListener('scroll', () => {
      content.classList.add('is-scrolling');
      clearTimeout(timer);
      timer = setTimeout(() => content.classList.remove('is-scrolling'), 1400);
    }, { passive: true });
  }

  async cartWithSection(cart) {
    const root = window.Shopify?.routes?.root || '/';
    const sectionResponse = await fetch(`${root}?sections=cart-drawer`, { cache: 'no-store' });
    if (!sectionResponse.ok) throw new Error('Unable to refresh your bag. Please try again.');
    const sections = await sectionResponse.json();
    cart.sections = sections;
    return cart;
  }

  async applyCartDiscount(form) {
    const input = form.querySelector('input[name="discount"]');
    const button = form.querySelector('button[type="submit"]');
    const code = input?.value.trim();
    if (!code || this.cartUpdating) return;
    this.cartUpdating = true;
    if (button) button.disabled = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'That promo code could not be submitted.');
      await window.BellyfulReplaceCart(await this.cartWithSection(await this.reconcileCartGift(cart)));
      const message = document.querySelector('[data-cart-message]');
      if (message) message.textContent = 'Code submitted. Shopify will confirm eligible savings here or at checkout.';
    } catch (error) {
      const message = document.querySelector('[data-cart-message]');
      if (message) message.textContent = error.message || 'That promo code could not be submitted.';
      if (button) button.disabled = false;
    } finally {
      this.cartUpdating = false;
    }
  }

  // Keep a previously accepted gift from lingering if the paid cart loses eligibility.
  // No effect whatsoever while the optional gift feature is switched off.
  cartGiftEligible(cart) {
    const drawer = document.querySelector('#BellyfulCartDrawer');
    if (drawer?.dataset.giftEnabled !== 'true' || !cart?.items) return false;
    const paid = cart.items.filter(item => String(item.properties?._bellyful_gift) !== 'true');
    const count = paid.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const subscribe = paid.some(item => Boolean(item.selling_plan_allocation || item.selling_plan));
    const giftValue = cart.items.filter(item => String(item.properties?._bellyful_gift) === 'true').reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
    const paidSubtotal = Math.max(0, Number(cart.total_price || 0) - giftValue);
    return drawer.dataset.giftRule === 'spend'
      ? count > 0 && paidSubtotal >= Number(drawer.dataset.giftThreshold || 0)
      : count >= 3 || (drawer.dataset.celebrateSubscriptions === 'true' && subscribe);
  }

  async reconcileCartGift(cart) {
    const drawer = document.querySelector('#BellyfulCartDrawer');
    if (drawer?.dataset.giftEnabled !== 'true' || !cart?.items) return cart;
    const gifts = cart.items.filter(item => String(item.properties?._bellyful_gift) === 'true');
    if (!gifts.length) return cart;
    const qualified = this.cartGiftEligible(cart);
    const invalidGift = gifts.length > 1 || gifts.some(gift => Number(gift.final_line_price) !== 0 || Number(gift.quantity) !== 1);
    if (qualified && !invalidGift) return cart;
    const root = window.Shopify?.routes?.root || '/';
    for (const gift of gifts) {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: gift.key, quantity: 0, sections: 'cart-drawer', sections_url: window.location.pathname })
      });
      if (!response.ok) throw new Error('Unable to refresh the complimentary gift.');
      cart = await response.json();
    }
    return cart;
  }

  async addCartGift(button) {
    const variantId = Number(button.dataset.giftVariant);
    if (!variantId || this.cartUpdating) return;
    this.cartUpdating = true;
    button.disabled = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const beforeResponse = await fetch(`${root}cart.js`, {headers:{Accept:'application/json'}});
      if (!beforeResponse.ok) throw new Error('Unable to check gift eligibility. Please try again.');
      const before = await beforeResponse.json();
      const existing = before.items?.some(item => String(item.properties?._bellyful_gift) === 'true');
      if (existing) {
        await window.BellyfulReplaceCart(await this.cartWithSection(await this.reconcileCartGift(before)));
        return;
      }
      if (!this.cartGiftEligible(before)) {
        await window.BellyfulReplaceCart(await this.cartWithSection(before));
        throw new Error('Your bag no longer qualifies for this gift.');
      }
      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _bellyful_gift: 'true' } }] })
      });
      const added = await addResponse.json();
      if (!addResponse.ok) throw new Error(added.description || 'The gift could not be added.');
      const cartResponse = await fetch(`${root}cart.js`);
      if (!cartResponse.ok) throw new Error('Unable to verify the gift. Please review your bag before checking out.');
      let cart = await cartResponse.json();
      const giftLine = cart.items.find((item) => String(item.properties?._bellyful_gift) === 'true' && Number(item.variant_id) === variantId);
      if (!giftLine || Number(giftLine.final_line_price) !== 0) {
        if (giftLine) {
          const removeResponse = await fetch(`${root}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: giftLine.key, quantity: 0 })
          });
          if (!removeResponse.ok) {
            await window.BellyfulReplaceCart(await this.cartWithSection(cart));
            throw new Error('The gift discount is not active. Please remove the charged gift from your bag before checking out.');
          }
          cart = await removeResponse.json();
        }
        await window.BellyfulReplaceCart(await this.cartWithSection(cart));
        throw new Error('The gift discount is not active, so the item was not added to your order.');
      }
      await window.BellyfulReplaceCart(await this.cartWithSection(await this.reconcileCartGift(cart)));
    } catch (error) {
      const message = document.querySelector('[data-cart-message]');
      if (message) message.textContent = error.message || 'The gift could not be added.';
      if (button.isConnected) button.disabled = false;
    } finally {
      this.cartUpdating = false;
    }
  }

  celebrateCart(cart, force = false) {
    const drawer = document.querySelector('#BellyfulCartDrawer');
    const context = this.cartCelebrationContext;
    this.cartCelebrationContext = null;
    this.cartCelebratePending = false;
    if (!drawer || !force || !context || drawer.dataset.confettiEnabled !== 'true') return;
    const paidItems = (cart.items || []).filter(item => String(item.properties?._bellyful_gift) !== 'true');
    const count = paidItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const subscriptions = paidItems.filter(item => Boolean(item.selling_plan_allocation || item.selling_plan)).length;
    const oldCount = Number(context.count || 0);
    const oldSubs = Number(context.subscriptions || 0);
    const countUp = count > oldCount;
    const newSubscription = subscriptions > oldSubs;
    if (!countUp && !newSubscription) return;
    // Paid quantity milestones re-arm naturally after the confirmed count drops
    // below three. Subscription celebration remains a separate merchant option.
    const crossedQuantityMilestone = oldCount < 3 && count >= 3;
    const stage = (crossedQuantityMilestone || (drawer.dataset.celebrateSubscriptions === 'true' && newSubscription)) ? 3 :
      countUp ? Math.min(2, count) : 0;
    if (!stage) return;
    const giftModal = drawer.querySelector('.bf-cart-gift-modal');
    if (giftModal) giftModal.hidden = true;
    if (window.BellyfulCartFX && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.BellyfulCartFX.play(stage, () => this.offerCartGift());
    } else {
      this.offerCartGift();
    }
  }

  offerCartGift() {
    const drawer = document.querySelector('#BellyfulCartDrawer');
    if (this.giftDeclined || !drawer?.classList.contains('is-open')) return;
    const modal = drawer.querySelector('.bf-cart-gift-modal');
    if (!modal || !modal.hidden) return;
    modal.hidden = false;
    modal.querySelector('[data-cart-decline-gift]')?.focus({ preventScroll: true });
  }

}

window.BellyfulReplaceCart = async function BellyfulReplaceCart(cartData) {
  const status = document.querySelector('#BellyfulCartStatus');
  if (status) status.textContent = (status.dataset.updatedLabel || 'Bag updated. [count] items.').replace('[count]', String(cartData.item_count ?? (cartData.items || []).reduce((n, item) => n + Number(item.quantity || 0), 0)));
  document.querySelectorAll('[data-cart-count]').forEach((element) => {
    const paidCount = (cartData.items || []).reduce((n, item) => n + (String(item.properties?._bellyful_gift) === 'true' ? 0 : Number(item.quantity || 0)), 0);
    element.textContent = paidCount;
    element.hidden = paidCount === 0;
  });

  let html = cartData.sections?.['cart-drawer'];
  const current = document.querySelector('#BellyfulCartDrawer');
  // Shopify may return HTTP 200 with a null section. Recover only its display;
  // never repeat a cart mutation whose outcome might already be successful.
  const isDrawer = value => {
    if (typeof value !== 'string' || !value.trim()) return false;
    const probe = document.createElement('div');
    probe.innerHTML = value;
    return Boolean(probe.querySelector('#BellyfulCartDrawer'));
  };
  if (current && !isDrawer(html)) {
    try { html = (await window.BellyfulTheme.cartWithSection(cartData)).sections?.['cart-drawer']; }
    catch (_) { html = null; }
    if (!isDrawer(html)) {
      window.BellyfulTheme.cartCelebratePending = false;
      window.BellyfulTheme.cartCelebrationContext = null;
      window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      return false;
    }
  }
  if (html && current) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    const fresh = wrapper.querySelector('#BellyfulCartDrawer');
    if (fresh) {
      const wasOpen = current.classList.contains('is-open');
      const pendingFocus = window.BellyfulTheme.cartFocus;
      const hadFocus = Boolean(pendingFocus) || current.contains(document.activeElement);
      const focusedKey = pendingFocus?.key || document.activeElement?.dataset?.cartKey;
      const focusedAction = pendingFocus?.action || (document.activeElement?.hasAttribute('data-cart-remove') ? 'data-cart-remove' : 'data-cart-quantity');
      const focusedDelta = pendingFocus?.delta ?? document.activeElement?.dataset?.cartQuantity;
      current.replaceWith(fresh);
      if (wasOpen) {
        fresh.hidden = false;
        fresh.classList.add('is-open');
        fresh.setAttribute('aria-hidden', 'false');
        window.BellyfulTheme.enableFocusTrap(fresh);
        window.BellyfulTheme.cleanPlanLabels(fresh);
        if (hadFocus) {
          const replacementControl = [...fresh.querySelectorAll(`[${focusedAction}]`)].find((control) => control.dataset.cartKey === focusedKey && (focusedAction !== 'data-cart-quantity' || control.dataset.cartQuantity === focusedDelta));
          (replacementControl || fresh.querySelector('[data-cart-close]:not([tabindex="-1"])'))?.focus({ preventScroll: true });
        }
      }
      if (window.BellyfulTheme) {
        window.BellyfulTheme.setupCartScrollbar(document);
        const active = window.BellyfulTheme.cartCelebratePending === true;
        if (wasOpen) window.BellyfulTheme.celebrateCart(cartData, active);
        else if (active) setTimeout(() => window.BellyfulTheme.celebrateCart(cartData, true), 130);
        else window.BellyfulTheme.celebrateCart(cartData, false);
      }
    }
  }
  return true;
};

window.BellyfulRefreshCart = async function BellyfulRefreshCart(open = true) {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}cart.js`);
  if (!response.ok) throw new Error('Unable to refresh your bag. Please try again.');
  const cart = await response.json();

  const sectionResponse = await fetch(`${root}?sections=cart-drawer`, { cache: 'no-store' });
  if (!sectionResponse.ok) throw new Error('Unable to refresh your bag. Please try again.');
  const sections = await sectionResponse.json();
  cart.sections = sections;
  const replaced = await window.BellyfulReplaceCart(await window.BellyfulTheme.reconcileCartGift(cart).then(async normalized => normalized === cart ? cart : window.BellyfulTheme.cartWithSection(normalized)));

  if (open && replaced) {
    const drawer = document.querySelector('#BellyfulCartDrawer');
    if (drawer) window.BellyfulTheme.openDrawer('#BellyfulCartDrawer', null);
  }
};

window.BellyfulTheme = new BellyfulTheme();
